// Khối dùng chung cho trang Đối soát: nút sao chép, nhãn trạng thái phiếu, tách số tiền theo loại.

import { useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { toast } from 'sonner'
import { money } from '@/lib/format'
import { cn } from '@/lib/utils'
import { SignedAmount } from '@/components/orders/order-money'
import { KIND_LABEL, VOUCHER_STATUS, sortedKinds } from './types'

/** Nút sao chép một giá trị (số tài khoản, nội dung chuyển khoản…). */
export function CopyButton({ value, label, className }: { value: string; label: string; className?: string }) {
  const [done, setDone] = useState(false)
  return (
    <button
      type="button"
      onClick={() =>
        navigator.clipboard.writeText(value).then(
          () => {
            setDone(true)
            toast.success(`Đã sao chép ${label}`)
            setTimeout(() => setDone(false), 1500)
          },
          () => toast.error('Trình duyệt chặn sao chép'),
        )
      }
      aria-label={`Sao chép ${label}`}
      className={cn(
        'motion-state inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-lg border border-rule bg-raise px-3 text-sm font-medium text-ink hover:bg-paperSoft desktop:min-h-9',
        className,
      )}
    >
      {done ? <Check className="size-4 text-ok" aria-hidden /> : <Copy className="size-4" aria-hidden />}
      {done ? 'Đã chép' : 'Sao chép'}
    </button>
  )
}

export function VoucherStatusBadge({ status, className }: { status: string; className?: string }) {
  const meta = VOUCHER_STATUS[status] ?? { text: status, cls: 'bg-paperSoft text-inkSoft' }
  return (
    <span className={cn('inline-flex max-w-full items-start gap-1.5 rounded-full px-2 py-0.5 text-left text-xs font-medium', meta.cls, className)}>
      <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-current" aria-hidden />
      <span className="min-w-0 break-words">{meta.text}</span>
    </span>
  )
}

/** Tổng số tiền của tôi trên đơn + các khoản tách theo loại (khi có nhiều hơn một khoản). */
export function AmountByKind({ amount, byKind, className }: { amount: number; byKind: Record<string, number>; className?: string }) {
  const kinds = sortedKinds(byKind)
  const single = kinds.length === 1 ? kinds[0]! : null
  return (
    <span className={cn('flex min-w-0 flex-col items-end gap-0.5 mobile:items-start', className)}>
      <SignedAmount value={amount} className="font-semibold" />
      {single ? (
        <span className="text-xs text-muted">{KIND_LABEL[single[0]] ?? 'Khoản khác'}</span>
      ) : (
        kinds.map(([k, v]) => (
          <span key={k} className="whitespace-nowrap text-xs text-inkSoft">
            {KIND_LABEL[k] ?? 'Khoản khác'}{' '}
            <span className={cn('font-mono tabular-nums', v < 0 ? 'text-danger' : 'text-ink')}>
              {v > 0 ? '+' : ''}
              {money(v)}
            </span>
          </span>
        ))
      )}
    </span>
  )
}
